import React, { useState, useEffect, useRef } from 'react';
import T from 'prop-types';
import { Controlled as Zoom } from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import Icon from '@ably/ui/core/Icon';

function useInterval(callback, delay) {
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (delay === null) {
      return;
    }

    const id = setInterval(() => savedCallback.current(), delay);

    // eslint-disable-next-line consistent-return
    return () => clearInterval(id);
  }, [delay]);
}

const AssetTrackingDiagram = ({ images: { withoutAbly, withAbly } }) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const [currentImage, setCurrentImage] = useState('withoutAbly');
  const [autoPlay, setAutoPlay] = useState(true);
  const swapImages = () => setCurrentImage(currentImage === 'withoutAbly' ? 'withAbly' : 'withoutAbly');
  const setImage = (img) => {
    setCurrentImage(img);
    setAutoPlay(false);
  };

  useInterval(swapImages, autoPlay ? 5000 : null);

  const handleClick = () => {
    if (isZoomed) {
      setIsZoomed(false);
    } else {
      setIsZoomed(true);
      setAutoPlay(false);
    }
  };

  return (
    <>
      <div className="mb-40">
        <button
          className={`${
            currentImage === 'withoutAbly' ? 'ws-integrate-diagram-btn ' : 'ws-integrate-diagram-btn-inactive'
          } mr-16`}
          data-id="without-ably-btn"
          onClick={() => setImage('withoutAbly')}
        >
          Without Ably <span className="sm:inline hidden">Asset Tracking</span>
        </button>
        <button
          className={currentImage === 'withAbly' ? 'ws-integrate-diagram-btn' : 'ws-integrate-diagram-btn-inactive'}
          data-id="with-ably-btn"
          onClick={() => setImage('withAbly')}
        >
          With Ably <span className="sm:inline hidden">Asset Tracking</span>
        </button>
      </div>
      <div className="relative mb-40 flex items-center justify-center">
        <div className="ws-lightbox-zoom-icon">
          <Icon name="icon-gui-magnifying-glass-plus-outline" color="text-white" size="1.5rem" additionalCSS="p-0" />
        </div>
        <div className={`${currentImage === 'withoutAbly' ? 'ws-diagram-show' : ''} ws-diagram`} onClick={handleClick}>
          <Zoom isZoomed={isZoomed && currentImage === 'withoutAbly'}>
            <img src={withoutAbly.src[0]} alt={withoutAbly.alt} srcSet={`${withoutAbly.src[1]}, 2x`} />
          </Zoom>
        </div>

        <div
          className={`${currentImage === 'withAbly' ? 'ws-diagram-show' : ''} ws-diagram absolute h-full block`}
          onClick={handleClick}
        >
          <Zoom wrapStyle={{ height: '100%' }} isZoomed={isZoomed && currentImage === 'withAbly'}>
            <img
              key={withAbly.alt}
              src={withAbly.src[0]}
              alt={withAbly.alt}
              srcSet={`${withAbly.src[1]}, 2x`}
              className="mx-auto h-full block"
            />
          </Zoom>
        </div>
      </div>
      <div className="mb-96 md:mb-128 relative">
        <p
          className={`${currentImage === 'withoutAbly' ? 'visible' : 'invisible'} ui-text-p2 font-light text-dark-grey`}
        >
          A typically complex realtime asset tracking cloud architecture using AWS services
        </p>
        <p
          className={`${
            currentImage === 'withAbly' ? 'visible' : 'invisible'
          } ui-text-p2 font-light text-dark-grey absolute left-0 right-0 mx-auto `}
        >
          Ably Asset Tracking simplifies cloud architecture so Dev & Ops teams can refocus on their core product
          engineering
        </p>
      </div>
    </>
  );
};

const ImageT = T.shape({
  src: T.arrayOf(T.string),
  alt: T.string,
});

AssetTrackingDiagram.propTypes = {
  images: T.shape({
    withoutAbly: ImageT,
    withAbly: ImageT,
  }),
};

export default AssetTrackingDiagram;
